import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { portfolioData } from '../mock';
import { ArrowLeft, Play, Volume2, VolumeX, X } from 'lucide-react';

const FashionVideoCard = ({ video, index, total, onOpen }) => {
  const videoRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleEnter = () => {
    setIsHovered(true);
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };

  const handleLeave = () => {
    setIsHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      className="group relative cursor-pointer overflow-hidden bg-zinc-900"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onClick={() => onOpen(video)}
      style={{
        animation: `fade-in-up 0.6s ease-out ${index * 0.08}s both`
      }}
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <video
          ref={videoRef}
          src={video.videoUrl}
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none"></div>
        
        <div className="absolute top-6 left-6">
          <span className="text-xs tracking-[0.3em] text-white/70 uppercase font-mono">
            {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
          </span>
        </div>

        <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${isHovered ? 'opacity-0' : 'opacity-100'}`}>
          <div className="w-14 h-14 rounded-full border border-white/40 backdrop-blur-sm flex items-center justify-center group-hover:border-white group-hover:scale-110 transition-all duration-300">
            <Play className="w-4 h-4 text-white fill-white ml-0.5" />
          </div>
        </div>

        <div className="absolute bottom-6 left-6 right-6">
          <h3 className="text-xl md:text-2xl font-black text-white tracking-tight uppercase">
            {video.title}
          </h3>
        </div>
      </div>
    </div>
  );
};

const Fashion = () => {
  const [activeVideo, setActiveVideo] = useState(null);
  const [muted, setMuted] = useState(false);
  const [activeSection, setActiveSection] = useState(portfolioData.fashionSections[0]?.id);
  const modalVideoRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Track active section while scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );

    portfolioData.fashionSections.forEach((section) => {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const closeModal = () => {
    if (modalVideoRef.current) {
      modalVideoRef.current.pause();
    }
    setActiveVideo(null);
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-black/80 backdrop-blur-xl border-b border-zinc-900">
        <nav className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link 
            to="/" 
            className="text-xl font-black tracking-tighter text-white hover:text-cyan-400 transition-colors"
          >
            SLOOMX
          </Link>
          <Link 
            to="/"
            className="group flex items-center gap-2 text-sm text-zinc-400 hover:text-white font-medium transition-colors uppercase tracking-wider"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Back Home
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="pt-40 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-fade-in-up">
            <span className="text-sm tracking-[0.3em] text-cyan-400 uppercase font-medium mb-6 block">
              Editorial · Style · Motion
            </span>
            <h1 className="text-7xl md:text-9xl font-black text-white tracking-tighter uppercase leading-none mb-8">
              Fashion
            </h1>
            <p className="text-xl md:text-2xl text-zinc-400 max-w-3xl leading-relaxed">
              A curated collection of fashion-driven edits, exploring movement, texture, and the language of style through cinematic storytelling.
            </p>
          </div>
        </div>
      </section>

      {/* Sticky Section Navigator */}
      <div className="sticky top-[88px] z-30 bg-black/90 backdrop-blur-xl border-y border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-wrap gap-3 overflow-x-auto">
            {portfolioData.fashionSections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`px-5 py-2 text-xs uppercase tracking-[0.2em] font-medium transition-all duration-300 whitespace-nowrap ${
                  activeSection === section.id
                    ? 'bg-white text-black'
                    : 'bg-transparent text-zinc-400 hover:text-white border border-zinc-800 hover:border-zinc-600'
                }`}
              >
                {section.title}
                <span className="ml-2 text-zinc-500">{section.videos.length}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="px-6 pb-24">
        <div className="max-w-7xl mx-auto">
          {portfolioData.fashionSections.map((section, sectionIndex) => (
            <section 
              key={section.id} 
              id={section.id}
              className={`scroll-mt-44 ${sectionIndex > 0 ? 'pt-24 mt-24 border-t border-zinc-900' : 'pt-12'}`}
            >
              {/* Section Header */}
              <div className="mb-12">
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="text-sm font-mono text-zinc-600">
                    {String(sectionIndex + 1).padStart(2, '0')}
                  </span>
                  <h2 className="text-4xl md:text-6xl font-black text-white tracking-tight uppercase">
                    {section.title}
                  </h2>
                </div>
                <p className="text-lg text-zinc-500 max-w-2xl ml-12">
                  {section.description}
                </p>
              </div>

              {/* Video Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {section.videos.map((video, index) => (
                  <FashionVideoCard
                    key={video.id}
                    video={video}
                    index={index}
                    total={section.videos.length}
                    onOpen={setActiveVideo}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>

      {/* Quote section */}
      <section className="px-6 py-24 border-t border-zinc-900">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-2xl md:text-4xl text-white font-light leading-tight tracking-tight">
            "Fashion is the armor to survive the reality of everyday life."
          </p>
          <p className="mt-6 text-sm text-zinc-500 uppercase tracking-[0.3em]">
            — Bill Cunningham
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-zinc-900 py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Link 
            to="/"
            className="text-2xl font-black tracking-tighter text-white hover:text-cyan-400 transition-colors"
          >
            SLOOMX
          </Link>
          <p className="text-zinc-600 text-sm mt-4">
            © {new Date().getFullYear()} Sloomx. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Fullscreen Modal */}
      {activeVideo && (
        <div 
          className="fixed inset-0 bg-black z-50 flex items-center justify-center animate-fade-in"
          onClick={closeModal}
        >
          <button
            onClick={closeModal}
            className="absolute top-6 right-6 z-10 text-white hover:text-cyan-400 transition-colors p-2"
          >
            <X className="h-8 w-8" />
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMuted(!muted);
              if (modalVideoRef.current) {
                modalVideoRef.current.muted = !muted;
              }
            }}
            className="absolute top-6 right-20 z-10 text-white hover:text-cyan-400 transition-colors p-2"
          >
            {muted ? <VolumeX className="h-7 w-7" /> : <Volume2 className="h-7 w-7" />}
          </button>

          <div 
            className="relative w-full h-full flex items-center justify-center p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              ref={modalVideoRef}
              src={activeVideo.videoUrl}
              autoPlay
              loop
              playsInline
              muted={muted}
              controls
              className="max-w-full max-h-[90vh] w-auto h-auto animate-scale-in"
            />
          </div>

          <div className="absolute bottom-8 left-8 z-10">
            <span className="text-xs tracking-[0.3em] text-cyan-400 uppercase font-medium mb-2 block">
              Fashion Edit
            </span>
            <h2 className="text-3xl font-black text-white tracking-tight uppercase">
              {activeVideo.title}
            </h2>
          </div>
        </div>
      )}
    </div>
  );
};

export default Fashion;
